import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import FullScreenView from '../../components/FullScreenView';
import { createRental } from '../../api/services/rentalsService';
import CurrencySelector from '../../components/CurrencySelector';

interface PaymentFormData {
  cardNumber: string;
  cardHolder: string;
  expiryDate: string;
  cvv: string;
  amount: number;
}

interface BookingDetails {
  vehicle: any;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  totalPrice: number;
  totalHours: number;
}

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const bookingDetails: BookingDetails = location.state?.bookingDetails;

  const [formData, setFormData] = useState<PaymentFormData>({
    cardNumber: '',
    cardHolder: '',
    expiryDate: '',
    cvv: '',
    amount: bookingDetails?.totalPrice || 0
  });

  // Rediriger si aucun détail de réservation
  useEffect(() => {
    if (!bookingDetails) {
      navigate('/overview');
    }
  }, [bookingDetails, navigate]);

  const handleInputChange = (field: keyof PaymentFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (value: string) => {
    const formatted = formatCardNumber(value);
    handleInputChange('cardNumber', formatted);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    /*if (!token || !bookingDetails || !user?.id) {
      setMessage({ text: 'Erreur: Veuillez vous connecter', type: 'error' });
      return;
    }*/

    setIsLoading(true);
    setMessage(null);

    try {
      // Simulation d'un paiement
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Ici vous intégreriez votre logique de paiement réelle
      // const response = await processPayment(formData, bookingDetails);
      
      console.log('💳 Paiement simulé réussi, création du trajet...');
      
      // Vérifier les données du véhicule
      console.log('🔍 Détails du véhicule:', bookingDetails.vehicle);
      console.log('🔍 Propriétés du véhicule:', Object.keys(bookingDetails.vehicle));
      console.log('🔍 User ID:', user?.id);
      
      const vehicleId = bookingDetails.vehicle.id_vehicle?.toString() || bookingDetails.vehicle.id?.toString() || bookingDetails.vehicle._id?.toString();
      const ownerId = bookingDetails.vehicle.owner_id || null; // Si undefined/null dans véhicule, on met null
      
      // Récupérer l'ID utilisateur avec fallback
      const userAny = user as any;
      const renterId = (userAny?.id || userAny?.id_user || userAny?.user_id || userAny?.userId)?.toString();
      
      console.log('🔍 User complet:', userAny);
      console.log('🔍 User ID trouvé:', renterId);
      
      console.log('🔍 Vehicle ID RAA:', bookingDetails.vehicle);
      console.log('🔍 Owner ID RAA:', user);
      console.log('🔍 Vehicle ID extrait:', vehicleId);
      console.log('🔍 Owner ID extrait:', ownerId);
      console.log('🔍 Renter ID extrait:', renterId);
      console.log('🔍 Vehicle complet dans bookingDetails:', bookingDetails.vehicle);
            console.log('🔍 User complet:', user);
      
      if (!renterId) {
        throw new Error('Utilisateur non connecté - ID utilisateur manquant');
      }
      
      if (!vehicleId) {
        throw new Error('ID du véhicule manquant');
      }   
      
      

      // Créer le trajet via le service rental
      const rentalData = {
        vehicle_id: vehicleId,
        renter_id: renterId, // L'utilisateur qui loue le véhicule (validé)
        start_date: `${bookingDetails.startDate}T${bookingDetails.startTime}:00.000Z`,
        end_date: `${bookingDetails.endDate}T${bookingDetails.endTime}:00.000Z`,
        status: 'pending' as const,
        total_price: bookingDetails.totalPrice,
        source: 'app',
        owner_id: ownerId, // null si undefined dans le véhicule
        promo_code_id: null,
        discount_applied: 0,
        vehicle_type: bookingDetails.vehicle.vehicle_type || 'car',
        nb_kilometre: 0,
        lieu_start: bookingDetails.vehicle.location || 'Localisation non définie'
      };

      console.log('📝 Données du trajet à créer:', rentalData);
      console.log('🔍 Vehicle ID:', vehicleId);
      console.log('🔍 Owner ID:', ownerId);

      const createdRental = await createRental(rentalData, token ?? "");
      console.log('✅ Trajet créé avec succès:', createdRental);

      setMessage({ text: 'Paiement traité et trajet créé avec succès !', type: 'success' });
      
      // Rediriger vers la confirmation après 2 secondes
      setTimeout(() => {
        navigate('/booking-confirmation', {
          state: {
            bookingDetails,
            paymentDetails: formData,
            rentalId: createdRental?.id_rental || 'N/A',
            rentalResponse: createdRental // Passer la réponse complète incluant le trip_token
          }
        });
      }, 2000);

    } catch (error: any) {
      console.error('❌ Erreur lors du paiement ou création du trajet:', error);
      setMessage({ 
        text: error.response?.data?.message || 'Erreur lors du traitement du paiement', 
        type: 'error' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!bookingDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <FullScreenView
      isOpen={true}
      onClose={() => navigate('/overview')}
      title="Paiement sécurisé"
      className="p-0"
    >
      <div className="w-full space-y-6 px-4 sm:px-6 py-6 overflow-y-auto flex-1">
        {/* En-tête avec informations de réservation */}
        <div className="bg-gradient-to-r from-indigo-50 to-indigo-50 rounded-xl p-4 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
              <div className="text-3xl">💳</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-2">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">
                  Paiement sécurisé
                </h2>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                  Sécurisé
                </span>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 text-gray-600">
                <div className="flex items-center space-x-2">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <span className="text-sm truncate">Véhicule: {bookingDetails.vehicle.title}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <span className="text-sm truncate">Montant: {bookingDetails.totalPrice.toFixed(2)}€</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Résumé de la réservation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Résumé de la réservation</h3>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="text-3xl">{bookingDetails.vehicle.icon || '🚗'}</div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{bookingDetails.vehicle.title}</h4>
                    <p className="text-sm text-gray-600">Type: {bookingDetails.vehicle.type}</p>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Début:</span>
                    <span className="font-medium">
                      {new Date(bookingDetails.startDate).toLocaleDateString('fr-FR')} à {bookingDetails.startTime}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fin:</span>
                    <span className="font-medium">
                      {bookingDetails.endDate 
                        ? `${new Date(bookingDetails.endDate).toLocaleDateString('fr-FR')} à ${bookingDetails.endTime}`
                        : `${new Date(bookingDetails.startDate).toLocaleDateString('fr-FR')} à ${bookingDetails.endTime}`
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Durée:</span>
                    <span className="font-medium">{bookingDetails.totalHours} heure(s)</span>
                  </div>
                  <div className="flex justify-between border-t pt-3">
                    <span className="text-gray-900 font-semibold">Total:</span>
                    <span className="font-bold text-indigo-600 text-lg">{bookingDetails.totalPrice.toFixed(2)}€</span>
                  </div>
                  
                  {/* Sélecteur de devise */}
                  <div className="border-t pt-3 mt-3">
                    <CurrencySelector
                      amount={bookingDetails.totalPrice}
                      baseCurrency="EUR"
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Formulaire de paiement */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Informations de paiement</h3>

              {message && (
                <div className={`mb-4 p-4 rounded-lg ${
                  message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {message.text}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Numéro de carte */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Numéro de carte
                  </label>
                  <input
                    type="text"
                    value={formData.cardNumber}
                    onChange={(e) => handleCardNumberChange(e.target.value)}
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Titulaire de la carte */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Titulaire de la carte
                    </label>
                    <input
                      type="text"
                      value={formData.cardHolder}
                      onChange={(e) => handleInputChange('cardHolder', e.target.value.toUpperCase())}
                      placeholder="NOM PRÉNOM"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>

                  {/* Date d'expiration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date d'expiration
                    </label>
                    <input
                      type="text"
                      value={formData.expiryDate}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length >= 2) {
                          value = value.slice(0, 2) + '/' + value.slice(2, 4);
                        }
                        handleInputChange('expiryDate', value);
                      }}
                      placeholder="MM/AA"
                      maxLength={5}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                </div>

                {/* CVV */}
                <div className="w-full md:w-1/3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Code de sécurité (CVV)
                  </label>
                  <input
                    type="text"
                    value={formData.cvv}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      handleInputChange('cvv', value);
                    }}
                    placeholder="123"
                    maxLength={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                {/* Informations de sécurité */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <div className="text-sm text-gray-600">
                      <p className="font-medium text-gray-900 mb-1">Paiement sécurisé</p>
                      <p>Vos informations de paiement sont protégées par un chiffrement SSL de niveau bancaire.</p>
                    </div>
                  </div>
                </div>

                {/* Bouton de paiement */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-3 px-6 rounded-xl font-medium transition-colors ${
                    isLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Traitement du paiement...
                    </div>
                  ) : (
                    `Payer ${bookingDetails.totalPrice.toFixed(2)}€`
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </FullScreenView>
  );
};

export default Payment; 